import * as React from "react";
import { Box, Card, Chip, MenuItem, MenuList, Popover, Stack, Tooltip } from "@mui/material";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import { EyeIcon, ProhibitIcon, Rows, UploadIcon } from "@phosphor-icons/react";
import { PencilSimpleIcon } from "@phosphor-icons/react/dist/ssr/PencilSimple";
import { PlusIcon } from "@phosphor-icons/react/dist/ssr/Plus";
import { TrashIcon } from "@phosphor-icons/react/dist/ssr/Trash";
import { useNavigate } from "react-router-dom";

import {
	Contact,
	contactsKeys,
	FileMapping,
	useContactFields,
	useDeleteContact,
	useImportContacts,
} from "@/hooks/api/use-contacts";
import { useCreateSuppression } from "@/hooks/api/use-suppressions";
import { useCachedTableStateWithUrl } from "@/hooks/use-cached-table-state-with-url";
import { useColumnVisibility } from "@/hooks/use-column-visibility";
import { useDialog } from "@/hooks/use-dialog";
import { usePopover } from "@/hooks/use-popover";
import { ColumnSelector } from "@/components/core/column-selector/column-selector";
import { Dropdown } from "@/components/core/dropdown/dropdown";
import { DropdownPopover } from "@/components/core/dropdown/dropdown-popover";
import { DropdownTrigger } from "@/components/core/dropdown/dropdown-trigger";
import { CustomTable } from "@/components/customTable";
import { TableActions } from "@/components/customTable/table-actions";
import { Column, FilterOption, FilterType } from "@/components/customTable/types";
import { DeleteModal } from "@/components/deleteModal/delete-modal";
import { TableAnimation } from "@/components/table-animation/table-animation";

import { AttachToGroup } from "./bulkActions/attach-to-group";
import { DetachFromGroup } from "./bulkActions/detach-from-group";
import { CreateContact } from "./create";
import { FileMappingModal } from "./fileMappingModal/file-mapping-modal";
import { FILTER_SEPARATOR } from "@/constants";

interface GroupsChipsProps {
	groups: Array<{ id: string; name: string }>;
}

function GroupsChips({ groups }: GroupsChipsProps): React.JSX.Element {
	const popover = usePopover<HTMLDivElement>();
	const navigate = useNavigate();
	if (!groups || groups.length === 0) {
		return <Box sx={{ display: "flex", gap: 1 }} />;
	}

	const handleClick = (group: { id: string; name: string }) => {
		navigate(`/contacts?groupId=${group.name}${FILTER_SEPARATOR}${group.id}`);
	};

	const firstGroup = groups[0];
	const remainingGroups = groups.slice(1);
	const remainingCount = remainingGroups.length;

	return (
		<>
			<Box sx={{ display: "flex", gap: 1 }}>
				<Chip
					key={firstGroup.id}
					label={firstGroup.name}
					size="small"
					color="primary"
					onClick={() => handleClick(firstGroup)}
				/>
				{remainingCount > 0 && (
					<Box ref={popover.anchorRef}>
						<Chip
							label={`+${remainingCount}`}
							size="small"
							color="primary"
							variant="outlined"
							onClick={popover.handleOpen}
							sx={{ cursor: "pointer" }}
						/>
					</Box>
				)}
			</Box>
			{remainingCount > 0 && (
				<Popover
					open={popover.open}
					anchorEl={popover.anchorRef.current}
					onClose={popover.handleClose}
					anchorOrigin={{ horizontal: "left", vertical: "bottom" }}
					transformOrigin={{ horizontal: "left", vertical: "top" }}
					slotProps={{ paper: { sx: { p: 0, maxHeight: 300, minWidth: 200 } } }}
				>
					<MenuList sx={{ px: 0.5, py: 0.5 }}>
						{groups.slice(1).map((group) => (
							<MenuItem key={group.id} sx={{ fontSize: "14px", py: 1 }} onClick={() => handleClick(group)}>
								<Chip label={group.name} size="small" color="primary" />
							</MenuItem>
						))}
					</MenuList>
				</Popover>
			)}
		</>
	);
}

export function Contacts(): React.JSX.Element {
	const navigate = useNavigate();

	const [isCreateContactDrawerOpen, setIsCreateContactDrawerOpen] = React.useState(false);
	const [selectedContactToCreate, setSelectedContactToCreate] = React.useState<Contact | null>(null);
	const [contactToSuppress, setContactToSuppress] = React.useState<Contact | null>(null);
	const createSuppression = useCreateSuppression();

	const {
		items: contacts,
		isLoading,
		totalItems,
		refetchData: fetchContacts,
	} = useCachedTableStateWithUrl({
		queryKey: contactsKeys.lists(),
		endpoint: "/crm/contacts",
	});

	const {
		data: fields = [],
		isFetching: isFieldsFetching,
		isPending: isFieldsPending,
		isLoading: isFieldsLoading,
	} = useContactFields();
	const { selectedColumns, toggleColumn, resetToDefault, selectAllFields } = useColumnVisibility(fields);

	const isFieldsDisabled = isFieldsFetching || isFieldsPending || isFieldsLoading;

	const deleteContactMutation = useDeleteContact();
	const importContactsMutation = useImportContacts();

	const {
		data: selectedContactToDelete,
		open: isDeleteContactModalOpen,
		handleClose: handleDeleteContactModalClose,
		handleOpen: handleDeleteContactModalOpen,
	} = useDialog<Contact | null>();

	const {
		data: selectedFileMapping,
		open: isFileMappingModalOpen,
		handleClose: handleFileMappingModalClose,
		handleOpen: handleFileMappingModalOpen,
	} = useDialog<FileMapping | null>();

	const {
		data: isAttachToGroupModalOpenRows,
		open: isAttachToGroupModalOpen,
		handleClose: handleAttachToGroupModalClose,
		handleOpen: handleAttachToGroupModalOpen,
	} = useDialog<string[] | null>();

	const {
		data: isDetachFromGroupModalOpenRows,
		open: isDetachFromGroupModalOpen,
		handleClose: handleDetachFromGroupModalClose,
		handleOpen: handleDetachFromGroupModalOpen,
	} = useDialog<string[] | null>();

	const handleDeleteContact = async () => {
		if (selectedContactToDelete?.id) {
			await deleteContactMutation.mutateAsync(selectedContactToDelete.id);
			handleDeleteContactModalClose();
		}
	};

	const handleImportContacts = async (event: React.ChangeEvent<HTMLInputElement>) => {
		if (!event.target.files || event.target.files.length === 0) return;

		const file = event.target.files[0];

		try {
			const response = await importContactsMutation.mutateAsync(file);
			handleFileMappingModalOpen(response);
		} catch (error) {
			console.error("Upload failed:", error);
		} finally {
			event.target.value = "";
		}
	};

	// const handleExportContacts = () => {
	// 	console.log(contacts);
	// };

	const filtersOptions: FilterOption[] = React.useMemo(() => {
		return [
			{
				name: "groupId",
				label: "Group",
				value: "",
				type: FilterType.FETCH_SELECT,
				getOptionsUrl: "/crm/groups",
				fetchLabel: "name",
				fetchValue: "id",
			},
		];
	}, []);

	// const filtersDrawerOptions: FilterDrawerOption[] = React.useMemo(() => {
	// 	return [
	// 		{
	// 			name: "phone",
	// 			label: "Phone",
	// 			value: "",
	// 			type: DynamicFieldType.TEXT,
	// 		},
	// 		...fields.map((field) => ({
	// 			...field,
	// 			name: field.name,
	// 			label: field.label,
	// 			value: "",
	// 			type: field.type,
	// 		})),
	// 	];
	// }, [fields]);

	const availableFields = React.useMemo(() => {
		return [
			{ label: "Phone", name: "phone", type: "text" },
			{
				label: "Tags",
				name: "tags",
				type: "select",
				getOptionsUrl: "/crm/tags",
				fetchLabel: "label",
				fetchValue: "name",
			},
			...fields,
		];
	}, [fields]);

	const renderHeaderButtons = () => {
		return (
			<Dropdown>
				<DropdownTrigger>
					<Button
						startIcon={
							<Box component="span" sx={{ transform: "rotate(90deg)" }}>
								<Rows />
							</Box>
						}
						variant="outlined"
						color="secondary"
						disabled={fields.length === 0 || isFieldsDisabled}
					>
						Columns
					</Button>
				</DropdownTrigger>
				<DropdownPopover>
					<ColumnSelector
						fields={fields}
						selectedColumns={selectedColumns}
						onColumnToggle={toggleColumn}
						onResetToDefault={resetToDefault}
						onSelectAll={selectAllFields}
					/>
				</DropdownPopover>
			</Dropdown>
		);
	};

	const columns: Column[] = React.useMemo(() => {
		const allColumns: Column[] = [];

		const defaultColumns: Column[] = [
			{
				header: {
					id: "phone",
					label: "Phone",
					align: "left",
					sx: {
						fontWeight: "600",
					},
				},
				accessorKey: "phone",
				getCellValue: (row: any) => {
					return (
						<Tooltip title="View contact" arrow>
							<Box
								tabIndex={0}
								onClick={() => navigate(`/contacts/${row.id}/view`)}
								sx={{
									textDecoration: "none",
									color: "inherit",
									cursor: "pointer",
									width: "max-content",
									"&:hover": { textDecoration: "underline" },
								}}
							>
								{row.phone}
							</Box>
						</Tooltip>
					);
				},
			},
			{
				header: { id: "suppressed", label: "Do not call", align: "left", sx: { fontWeight: "600" } },
				accessorKey: "suppressed",
				getCellValue: (row: any) =>
					row.suppressed ? (
						<Tooltip title="On the account do-not-call list. No campaign will dial this number." arrow>
							<Chip
								label="Do not call"
								size="small"
								color="error"
								variant="filled"
								icon={<ProhibitIcon />}
								sx={{ cursor: "help" }}
							/>
						</Tooltip>
					) : null,
			},
		];

		const dynamicFields: Column[] = fields
			.filter((field) => selectedColumns.includes(field.name))
			.map((field) => ({
				header: {
					id: field.name,
					label: field.label || field.name,
					align: "left",
					sx: {
						fontWeight: "600",
					},
				},
				accessorKey: `attributes.${field.name}`,
				getCellValue: (row: any) => {
					const value = row.attributes?.[field.name];
					if (!value) return "";

					switch (field.type) {
						case "select": {
							return Array.isArray(value) ? value.join(", ") : value;
						}
						case "multiselect": {
							return Array.isArray(value) ? (
								<Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
									{value.map((item: any) => (
										<Chip key={item} label={item} size="small" variant="outlined" />
									))}
								</Box>
							) : (
								value
							);
						}
						case "checkbox": {
							return value ? "Yes" : "No";
						}
						case "boolean": {
							return value ? "Yes" : "No";
						}
						default: {
							return value;
						}
					}
				},
			}));

		const actionsColumn: Column = {
			header: {
				id: "actions",
				label: "",
				width: 100,
				align: "left",
				sx: {
					fontWeight: "600",
				},
			},
			accessorKey: "actions",
			getCellValue: (row: any) => (
				<Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end" }}>
					<TableActions
						actions={[
							{
								label: "View Details",
								icon: <EyeIcon width={20} height={20} weight="fill" color="var(--mui-palette-primary-main)" />,
								onClick: () => navigate(`/contacts/${row.id}/view`),
							},
							{
								label: "Edit",
								icon: <PencilSimpleIcon width={20} height={20} weight="fill" />,
								onClick: () => {
									setSelectedContactToCreate(row);
									setIsCreateContactDrawerOpen(true);
								},
							},
							{
								label: "Do not call",
								icon: <ProhibitIcon width={20} height={20} weight="fill" />,
								sx: { color: "error.main" },
								onClick: () => setContactToSuppress(row),
								hidden: Boolean(row.suppressed),
							},
							{
								label: "Delete",
								icon: <TrashIcon width={20} height={20} weight="fill" />,
								sx: { color: "error.main" },
								onClick: () => handleDeleteContactModalOpen(row),
							},
						]}
					/>
				</Box>
			),
		};

		allColumns.push(
			...defaultColumns,
			...dynamicFields,
			{
				header: {
					id: "groups",
					label: "Groups",
					align: "left",
					sx: {
						fontWeight: "600",
					},
				},
				accessorKey: "groups",
				getCellValue: (row: any) => {
					return <GroupsChips groups={row.groups || []} />;
				},
			},
			{
				header: {
					id: "tags",
					label: "Tags",
					align: "left",
					sx: {
						fontWeight: "600",
					},
				},
				accessorKey: "tags",
				getCellValue: (row: any) => {
					return (
						<Box sx={{ display: "flex", gap: 1 }}>
							{row.tags?.map((tag: any) => <Chip key={tag.id} label={tag.label} size="small" color="info" />)}
						</Box>
					);
				},
			},
			actionsColumn
		);

		return allColumns;
	}, [fields, selectedColumns, navigate, handleDeleteContactModalOpen]);

	const handleAttachToGroup = (rows: string[]) => {
		handleAttachToGroupModalOpen(rows);
	};

	const handleDetachFromGroup = (rows: string[]) => {
		handleDetachFromGroupModalOpen(rows);
	};

	const selectedRowActions = React.useMemo(() => {
		return [
			{
				label: "Attach to Group",
				onClick: handleAttachToGroup,
			},
			{
				label: "Detach from Group",
				onClick: handleDetachFromGroup,
			},
		];
	}, []);

	return (
		<React.Fragment>
			<Box
				sx={{
					maxWidth: "var(--Content-maxWidth)",
					m: "var(--Content-margin)",
					p: "var(--Content-padding)",
					width: "var(--Content-width)",
				}}
			>
				<Stack spacing={4}>
					<Stack
						direction={{ xs: "column", sm: "row" }}
						spacing={3}
						sx={{ alignItems: { xs: "flex-start", sm: "center" } }}
					>
						<Box sx={{ flex: "1 1 auto" }}>
							<Typography variant="h4">Contacts Management</Typography>
							<Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
								Manage contacts and their details
							</Typography>
						</Box>
						<Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, flexWrap: "wrap" }}>
							<input
								id="csv-upload"
								type="file"
								accept=".csv"
								style={{ display: "none" }}
								onChange={handleImportContacts}
							/>
							<Button
								startIcon={<UploadIcon />}
								variant="outlined"
								onClick={() => (document.querySelector("#csv-upload") as HTMLInputElement)?.click()}
								loading={importContactsMutation.isPending}
							>
								Import
							</Button>
							{/* <Button startIcon={<DownloadIcon />} variant="outlined" onClick={handleExportContacts}>
								Export
							</Button> */}
							{/* <Button startIcon={<Rows />} variant="outlined" onClick={() => navigate("/contacts/fields")}>
								Fields
							</Button>
							<Button startIcon={<UsersFourIcon />} variant="outlined" onClick={() => navigate("/contacts/groups")}>
								Groups
							</Button> */}
							<Button
								startIcon={<PlusIcon />}
								variant="contained"
								onClick={() => {
									setSelectedContactToCreate(null);
									setIsCreateContactDrawerOpen(true);
								}}
							>
								Create Contact
							</Button>
						</Box>
					</Stack>

					<TableAnimation delay={0}>
						<Card>
							<CustomTable
								columns={columns}
								data={contacts}
								refetchData={fetchContacts}
								isLoading={isLoading}
								renderHeaderButtons={renderHeaderButtons}
								totalItems={totalItems}
								hasSelectedRows
								selectedRowActions={selectedRowActions}
								filtersOptions={filtersOptions}
								// filtersDrawerOptions={filtersDrawerOptions}
								isFilterDrawerDisabled={isFieldsDisabled}
								availableFields={availableFields}
							/>
						</Card>
					</TableAnimation>
				</Stack>
			</Box>

			<DeleteModal
				open={Boolean(contactToSuppress)}
				onClose={() => setContactToSuppress(null)}
				onDelete={async () => {
					if (!contactToSuppress) return;
					await createSuppression.mutateAsync({ phone: contactToSuppress.phone });
					setContactToSuppress(null);
					fetchContacts();
				}}
				title="Add to do-not-call list"
				description={`${contactToSuppress?.phone ?? "This contact"} will not be dialled by any campaign in this account, now or in future. Any leads already waiting for this number are cancelled. You can lift this later from Settings \u2192 Do Not Call.`}
				deleteText="Add to do-not-call"
			/>

			<DeleteModal
				open={isDeleteContactModalOpen}
				onClose={handleDeleteContactModalClose}
				onDelete={handleDeleteContact}
				title="Delete Contact"
				description="Are you sure you want to delete this contact?"
			/>

			<FileMappingModal
				open={isFileMappingModalOpen}
				onClose={handleFileMappingModalClose}
				onSuccess={fetchContacts}
				fileMapping={selectedFileMapping!}
				fields={fields}
			/>

			<AttachToGroup
				open={isAttachToGroupModalOpen}
				onClose={handleAttachToGroupModalClose}
				rows={isAttachToGroupModalOpenRows!}
			/>

			<DetachFromGroup
				open={isDetachFromGroupModalOpen}
				onClose={handleDetachFromGroupModalClose}
				rows={isDetachFromGroupModalOpenRows!}
			/>

			<CreateContact
				isCreateContactDrawerOpen={isCreateContactDrawerOpen}
				onCreateDrawerClose={() => {
					setIsCreateContactDrawerOpen(false);

					setTimeout(() => {
						setSelectedContactToCreate(null);
					}, 300);
				}}
				contactId={selectedContactToCreate?.id || null}
			/>
		</React.Fragment>
	);
}
